import { AttachFileOutlined, ChevronRightRounded } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import { green, grey, red, yellow } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isFunction, isNil, size } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { htmlStringToText } from "../../_src_shared/utils/functions/htmlStringToText";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import CellRendererRequirementType from "../agGrid/cellRenderer/CellRendererRequirementType";
import Flx from "../layout/Flx";
import CommentsRenderer from "../renderers/CommentsRenderer";

const LoanConditionsTable = ({ rowData, onRowClicked }) => {
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Status",
        field: "status", // ['Open', 'In Progress',  'Accepted', 'Rejected', 'Second Level Review']
        width: 100,
        sort: "desc",
        cellStyle: { cursor: "pointer" },
        cellClass: "ag-cell-vertical-center",
      },
      {
        headerName: "Document Status",
        field: "docStipStatus", // ["Open Item", "Received - Pending Review", "Reviewed - Not Accepted", "Accepted"]
        width: 180,
        // sort: "desc",

        cellClass: "ag-cell-vertical-center",
        valueFormatter: (p) => {
          const status = p.value;

          if (isNil(status)) {
            return "N/A";
          }
          return status;
        },
        cellStyle: (params) => {
          const status = params.value;
          return {
            cursor: "pointer",
            opacity: isNil(status) ? 0.65 : 1,
            fontStyle: isNil(status) ? "italic" : "normal",
          };
        },
      },

      {
        headerName: "Requirement Type",
        field: "requirementType",
        width: 135,
        cellClass: "ag-cell-vertical-center",
        cellStyle: { cursor: "pointer" },
        cellRenderer: (params) => (
          <CellRendererRequirementType value={params.value} />
        ),
      },

      {
        headerName: "Requirement Details",
        field: "htmlString",
        minWidth: 250,
        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: 1.5,
          cursor: "pointer",
          paddingTop: 12,
          paddingBottom: 12,
          wrapText: true, // Wrap Text
          whiteSpace: "normal", // Allow normal white space handling
          autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
        },
        valueFormatter: (p) => {
          return htmlStringToText(p.value);
        },
      },
      {
        width: 60,
        cellClass: "ag-cell-vertical-center",
        cellStyle: { cursor: "pointer" },
        valueGetter: (p) => {
          const documentIds = p.data?.uploadedDocumentIds || [];
          return size(documentIds);
        },
        cellRenderer: (p) => {
          return <DocumentIdsCountBadge count={p.value} />;
        },
      },
      {
        width: 60,
        valueGetter: (p) => {
          const comments = p.data?.comments || p.data?.conditionComments || [];
          return comments?.length > 0 ? comments : null;
        },
        cellClass: "ag-cell-vertical-center",
        cellStyle: { cursor: "pointer" },
        cellRenderer: (p) => {
          return (
            <CommentsRenderer
              onlyCount
              comments={p.data?.comments || p.data?.conditionComments}
            />
          );
        },
      },
      {
        width: 50,
        cellClass: "ag-cell-vertical-center",
        cellStyle: { cursor: "pointer" },
        cellRenderer: (p) => {
          return (
            <Flx sx={{ height: "100%" }} center>
              <ChevronRightRounded sx={{ color: grey[600] }} />
            </Flx>
          );
        },
      },
    ],
    [],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const getRowStyle = useCallback((params) => {
    // ["Open Item", "Received - Pending Review", "Reviewed - Not Accepted", "Accepted"]
    // const status = params.data?.docStipStatus;
    // if (status === "Open Item") {
    //   return null;
    // }
    // if (status === "Accepted") {
    //   return {
    //     backgroundColor: green[50],
    //     borderTop: `1px solid #ffffff`,
    //     borderBottom: `1px solid #ffffff`,
    //   };
    //   //   return { backgroundColor: "#e8f5e9a9" };
    // }

    // if (status === "Received - Pending Review") {
    //   return {
    //     backgroundColor: yellow[50],
    //     borderTop: `1px solid #ffffff`,
    //     borderBottom: `1px solid #ffffff`,
    //   };
    // }

    // if (status === "Reviewed - Not Accepted") {
    //   return {
    //     backgroundColor: red[50],
    //     borderTop: `1px solid #ffffff`,
    //     borderBottom: `1px solid #ffffff`,
    //   };
    // }

    // return {};
    const status = params.data?.status;
    if (status === "Open") {
      return null;
    }
    if (status === "Accepted") {
      return {
        backgroundColor: green[50],
        borderTop: `1px solid #ffffff`,
        borderBottom: `1px solid #ffffff`,
      };
      //   return { backgroundColor: "#e8f5e9a9" };
    }
    if (status === "Rejected") {
      return {
        backgroundColor: red[50],
        borderTop: `1px solid #ffffff`,
        borderBottom: `1px solid #ffffff`,
      };
    }

    return {
      backgroundColor: yellow[50],
      borderTop: `1px solid #ffffff`,
      borderBottom: `1px solid #ffffff`,
    };
  }, []);

  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      tooltipComponent: () => (
        <Flx
          ac
          g={1}
          sx={{
            background: "#ffffff",
            border: `1px solid ${grey[300]}`,
            p: 1,
            borderRadius: 2,
          }}
        >
          Click row to address Loan Condition
          <ChevronRightRounded sx={{ color: grey[600] }} />
        </Flx>
      ),
      tooltipValueGetter: () => "Click to address condition",
      //   resizable: true, // Enable resizing
      //   initialWidth: 200, // Optional: Set a default size
      //   wrapText: true, // Wrap Text
      //   whiteSpace: "normal", // Allow normal white space handling
      //   autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
    }),
    [],
  );
  return (
    <Box
      sx={{
        // maxHeight: "800px",
        overflowY: "auto",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        ".ag-row-last": {
          borderBottom: "none !important",
        },
        ".ag-header-viewport": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <AgGridTableWrapper
        height="100%"
        simpleTable
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={getRowId}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          onRowClicked={onRowClicked}
          domLayout="autoHeight"
          tooltipShowDelay={100}
          enableCellTextSelection
          //   tooltipMouseTrack
        />
      </AgGridTableWrapper>
    </Box>
  );
};

const DocumentIdsCountBadge = memo(({ count, onClick }) => {
  return (
    <Tooltip
      title={
        isFunction(onClick)
          ? `${count} ${count === 1 ? "comment" : "comments"} — click to expand`
          : undefined
      }
    >
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: onClick ? "pointer" : "default",
          color: count > 0 ? "primary.main" : "text.secondary",
          "&:hover": onClick ? { color: "primary.dark" } : undefined,
        }}
      >
        <AttachFileOutlined sx={{ fontSize: 14 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
});

export default LoanConditionsTable;
